<template>
  <qtm-table v-if="purchaseItems.length" dense :headers="purchaseHeaders" :items="purchaseItems">
    <template v-slot:item.description="{ item }">
      <change-order-diff-value :value="item.description" />
      <div v-if="item.comment">
        <i>Comment:</i>
        &nbsp;
        <change-order-diff-value :value="item.comment" />
      </div>
    </template>
    <template v-slot:item.quantity="{ item }">
      <change-order-diff-value :value="quantityString(item)" />
    </template>
    <template v-slot:item.unit_price="{ item }">
      <change-order-diff-value currency :value="item.unit_price" />
    </template>
    <template v-slot:item.tax="{ item }">
      <change-order-diff-value :value="item.displayable_tax" />
    </template>
    <template v-slot:item.total_price="{ item }">
      <change-order-diff-value currency :value="item.total_price" />
    </template>
    <template v-slot:item.cost_code="{ item }">
      <change-order-diff-value :value="getCostCode(item.cost_code)" />
    </template>
  </qtm-table>
  <qtm-table v-if="rentalItems.length" dense :headers="rentalHeaders" :items="rentalItems">
    <template v-slot:item.description="{ item }">
      <change-order-diff-value :value="item.description" />
      <div v-if="item.comment">
        <i>Comment:</i>
        <change-order-diff-value :value="item.comment" />
      </div>
    </template>
    <template v-slot:item.quantity="{ item }">
      <change-order-diff-value :value="quantityString(item)" />
    </template>
    <template v-slot:item.rental_duration="{ item }">
      <change-order-diff-value :value="rentalDurationString(item)" />
    </template>
    <template v-slot:item.day_rate="{ item }">
      <change-order-diff-value currency :value="item.day_rate" />
    </template>
    <template v-slot:item.week_rate="{ item }">
      <change-order-diff-value currency :value="item.week_rate" />
    </template>
    <template v-slot:item.month_rate="{ item }">
      <change-order-diff-value currency :value="item.month_rate" />
    </template>
    <template v-slot:item.tax="{ item }">
      <change-order-diff-value :value="item.displayable_tax" />
    </template>
    <template v-slot:item.total_price="{ item }">
      <change-order-diff-value currency :value="item.total_price" />
    </template>
    <template v-slot:item.cost_code="{ item }">
      <change-order-diff-value :value="item.cost_code" />
    </template>
  </qtm-table>
</template>

<script>
import ChangeOrderDiffValue from '@/components/purchase-orders/change-order-diff-value.vue'
import QtmTable from '@/components/qtm-table.vue'

export default {
  name: 'change-order-table',
  components: { ChangeOrderDiffValue, QtmTable },
  props: {
    changeOrder: {
      type: Object,
      required: true,
    },
    costCodes: {
      type: Array,
      default: () => [],
    },
    showTax: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    commonHeaders() {
      return [
        { title: '#', value: 'item_number', width: 50, cellClass: 'text-center' },
        { title: 'Operation', value: 'operation', width: 82 },
        { title: 'Description', value: 'description', minWidth: 300 },
        { title: 'Quantity', value: 'quantity', width: 180 },
      ]
    },
    purchaseHeaders() {
      return this.commonHeaders.concat([
        { title: 'Unit Price', value: 'unit_price' },
        { title: 'Tax', value: 'tax', exclude: !this.showTax },
        { title: 'Subtotal', value: 'total_price', width: 220 },
        { title: 'Cost Code', value: 'cost_code' },
        ].filter(header => !header.exclude).map(header => ({ ...header, width: header.width || 180 })))
    },
    purchaseItems() {
      return this.changeOrder.items.filter(item => !item.rental_duration)
    },
    rentalHeaders() {
      return this.commonHeaders.concat([
        { title: 'Duration', value: 'rental_duration' },
        { title: 'Day Rate', value: 'day_rate' },
        { title: 'Week Rate', value: 'week_rate' },
        { title: 'Month Rate', value: 'month_rate' },
        { title: 'Tax', value: 'tax', exclude: !this.showTax },
        { title: 'Subtotal', value: 'total_price', width: 220 },
        { title: 'Cost Code', value: 'cost_code' },
        ].filter(header => !header.exclude).map(header => ({ ...header, width: header.width || 180 })))
    },
    rentalItems() {
      return this.changeOrder.items.filter(item => item.rental_duration)
    }
  },
  methods: {
    combinedValue(item, field1, field2) {
      if (typeof item[field1] === 'object' || typeof item[field2] === 'object') {
        const oldField1 = (typeof item[field1].old !== 'undefined') ? item[field1].old : item[field1]
        const oldField2 = (typeof item[field2].old !== 'undefined') ? item[field2].old : item[field2]

        const newField1 = (typeof item[field1].new !== 'undefined') ? item[field1].new : item[field1]
        const newField2 = (typeof item[field2].new !== 'undefined') ? item[field2].new : item[field2]

        return {
          old: `${oldField1} ${oldField2}`,
          new: `${newField1} ${newField2}`
        }
      }
      return `${item[field1]} ${item[field2] || ''}`
    },

    getCostCode(cc) {
      if (!cc) {
        return cc
      }

      if (cc.new) {
        return {
          old: cc.old,
          new: `${cc.new} ${this.costCodes.find(code => code.cost_code === cc.new)?.description ?? ''}`
        }
      }

      return `${cc} ${this.costCodes.find(code => code.cost_code === cc)?.description ?? ''}`
    },

    quantityString(item) {
      return this.combinedValue(item, 'quantity', 'unit')
    },

    rentalDurationString(item) {
      return this.combinedValue(item, 'rental_duration', 'rental_duration_unit')
    }
  }
}
</script>
